import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { GlowField } from "@/components/ui/GlowField";
import { CtaButton } from "@/components/ui/CtaButton";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { SubjectPicker, GradePicker } from "@/components/CurriculumPicker";
import { Sparkles, ClipboardList, Trash2, Printer, X } from "lucide-react";
import GenerateLoader from "@/components/ui/GenerateLoader";
import AttachmentPicker from "@/components/quiz/AttachmentPicker";

export default function HomeworkGenerator() {
  const [params] = useSearchParams();
  const initSubjectId = params.get("subjectId") || "";
  const [form, setForm] = useState({
    subject_id: initSubjectId,
    subject: params.get("subject") || "",
    grade_id: "",
    grade: params.get("grade") || "",
    topic: "",
    difficulty: "medium",
    objective: "",
    custom: "",
    count: 5,
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [opened, setOpened] = useState(null);
  const [attachmentText, setAttachmentText] = useState("");

  const load = async () => {
    const url = form.subject_id ? `/ai/homework?subject_id=${form.subject_id}` : "/ai/homework";
    const { data } = await api.get(url);
    setItems(data.homework || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [form.subject_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject_id) return toast.error("Pick a subject");
    if (!form.topic) return toast.error("Topic is required");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/homework/generate", {
        topic: form.topic,
        grade: form.grade,
        difficulty: form.difficulty,
        objective: form.objective,
        custom: form.custom,
        count: Number(form.count),
        subject_id: form.subject_id,
        attachment_text: attachmentText,
      });
      toast.success("Homework generated");
      setOpened(data.homework);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this homework?")) return;
    try { await api.delete(`/ai/homework/${id}`); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="homework-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1">/ ai homework</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter inline-flex items-center gap-2">
          <ClipboardList size={26} /> Generate homework in seconds
        </h1>
        <p className="text-sm text-neutral-600 mt-1">Topic + objective → AI builds questions, rubric & marking scheme. 8 credits.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-black/8 rounded-2xl p-6 grid sm:grid-cols-2 gap-3">
        <div>
          <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Subject *</div>
          <SubjectPicker
            testid="hw-subject"
            className="rounded-md"
            value={form.subject_id}
            onChange={(sid, sname, gid, gname) => setForm({ ...form, subject_id: sid, subject: sname, grade_id: gid || form.grade_id, grade: gname || form.grade })}
          />
        </div>
        <GlowField label="Topic" testid="hw-topic" required value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="e.g. Photosynthesis" />
        <div>
          <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Grade (optional)</div>
          <GradePicker
            testid="hw-grade"
            className="rounded-md"
            includeNone
            value={form.grade_id}
            onChange={(gid, gname) => setForm({ ...form, grade_id: gid, grade: gname })}
          />
        </div>
        <div>
          <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Difficulty</div>
          <SearchableSelect
            testid="hw-difficulty"
            placeholder="Difficulty"
            options={[{value:"easy",label:"Easy"},{value:"medium",label:"Medium"},{value:"hard",label:"Hard"}]}
            value={form.difficulty}
            onChange={(v) => setForm({ ...form, difficulty: v })}
          />
        </div>
        <GlowField label="Question count" type="number" min={3} max={20} value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} testid="hw-count" />
        <div className="sm:col-span-2"><GlowField label="Learning objective (optional)" as="textarea" rows={2} value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} testid="hw-objective" placeholder="What should students be able to do after this homework?" /></div>
        <div className="sm:col-span-2"><GlowField label="Custom instructions (optional)" as="textarea" rows={2} value={form.custom} onChange={(e) => setForm({ ...form, custom: e.target.value })} testid="hw-custom" placeholder="e.g. Mix of short + long answers, include a diagram question" /></div>
        <div className="sm:col-span-2"><AttachmentPicker onChange={setAttachmentText} /></div>
        <div className="sm:col-span-2">
          <CtaButton type="submit" variant="primary" size="lg" disabled={loading} data-testid="hw-submit" className="w-full rounded-md">
            <Sparkles size={16} /> {loading ? <GenerateLoader label="Generating homework" phrases={["Drafting problems…", "Setting difficulty…", "Adding an answer key…"]} /> : "Generate homework"}
          </CtaButton>
        </div>
      </form>

      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">/ your homework</div>
        {items.length === 0 ? (
          <div className="bg-white border border-dashed border-black/15 p-8 text-center text-sm text-neutral-500 rounded-2xl">No homework yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((h) => (
              <div key={h.id} className="bg-white border border-black/8 rounded-2xl p-4" data-testid={`hw-card-${h.id}`}>
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">{h.difficulty}</div>
                <div className="font-display font-semibold text-sm mt-1">{h.title}</div>
                <div className="text-xs text-neutral-500">{h.topic}{h.grade ? ` · ${h.grade}` : ""}</div>
                <div className="flex justify-between items-center mt-3">
                  <button onClick={() => setOpened(h)} className="text-xs font-semibold border border-black/15 hover:border-[#0A0C10] rounded-full px-3 py-1.5">Open</button>
                  <button onClick={() => del(h.id)} className="text-xs px-2 py-1.5 border border-red-200 hover:bg-red-50 text-red-500 rounded-md"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {opened && <HomeworkModal hw={opened} onClose={() => setOpened(null)} />}
    </div>
  );
}

function HomeworkModal({ hw, onClose }) {
  const d = hw.data || {};
  const print = () => window.print();
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl rounded-2xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 print:hidden">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">/ homework</div>
            <h2 className="font-display font-bold text-2xl">{d.title || hw.title}</h2>
          </div>
          <div className="flex gap-2">
            <button onClick={print} className="text-xs px-3 py-1.5 border border-black/15 hover:border-[#0A0C10] rounded-full inline-flex items-center gap-1"><Printer size={12} /> Print</button>
            <button onClick={onClose}><X size={18} /></button>
          </div>
        </div>
        <div className="space-y-3 text-sm">
          <div className="text-xs text-neutral-500">~{d.estimated_minutes || "—"} min · {(d.questions || []).length} questions</div>
          {(d.questions || []).map((q, i) => (
            <div key={i} className="border-t border-neutral-100 pt-3">
              <div className="font-medium">Q{i + 1}. {q.q} <span className="text-xs text-neutral-500">({q.marks || 1} marks · {q.type})</span></div>
              {q.answer && <div className="text-xs text-neutral-500 mt-1"><strong>Answer:</strong> {q.answer}</div>}
            </div>
          ))}
          {d.marking_scheme && (
            <div className="mt-4 bg-[#FBFAF7] border border-black/8 rounded-lg p-3 text-xs">
              <strong>Marking scheme:</strong> {d.marking_scheme}
            </div>
          )}
          {(d.rubric || []).length > 0 && (
            <div className="text-xs">
              <strong>Rubric:</strong>
              <ul className="list-disc pl-5 mt-1">{d.rubric.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
